import React, { useState, useEffect } from "react";
import { Loader2, X } from "lucide-react";
import axios from "axios";
import NavbarComptable from '../components/NavbarComptable';
import SidebarComptable from '../components/SidebarComptable';


const MonthlyDataComptable = () => {

    const API_URL = "https://api-ec.cinef-expertises.com/api";
    const token = localStorage.getItem("expert_token");

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");

    const [selectedClient, setSelectedClient] = useState(null);

    const [form, setForm] = useState({
        client_id: "",
        mois: "",
        annee: new Date().getFullYear(),
        ca_mensuel: "",
        ca_cumule: "",
        resultat_mensuel: "",
        resultat_cumule: "",
        tresorerie: "",
        details_financiers: "",
        evolution: "",
        commentaire: "",
    });

    // États pour les détails financiers
    const [detailsFinanciers, setDetailsFinanciers] = useState([]);
    const [selectedDetailType, setSelectedDetailType] = useState("");
    const [detailMontant, setDetailMontant] = useState("");
    const [autreDetailLabel, setAutreDetailLabel] = useState("");

    // États pour l'évolution mensuelle
    const [evolutionMensuelle, setEvolutionMensuelle] = useState([]);
    const [selectedMois, setSelectedMois] = useState("");
    const [evolutionMontant, setEvolutionMontant] = useState("");

    // Options prédéfinies pour les détails financiers
    const detailsFinanciersOptions = [
        { value: "charges", label: "Charges" },
        { value: "salaires", label: "Salaires" },
        { value: "petites_depenses", label: "Petites dépenses" },
        { value: "investissements", label: "Investissements" },
        { value: "frais_generaux", label: "Frais généraux" },
        { value: "impots_taxes", label: "Impôts et taxes" },
        { value: "autres", label: "Autres" }
    ];

    // Mois pour l'évolution
    const moisOptions = [
        { value: "janvier", label: "Janvier" },
        { value: "fevrier", label: "Février" },
        { value: "mars", label: "Mars" },
        { value: "avril", label: "Avril" },
        { value: "mai", label: "Mai" },
        { value: "juin", label: "Juin" },
        { value: "juillet", label: "Juillet" },
        { value: "aout", label: "Août" },
        { value: "septembre", label: "Septembre" },
        { value: "octobre", label: "Octobre" },
        { value: "novembre", label: "Novembre" },
        { value: "decembre", label: "Décembre" }
    ];

    //  1. Récupérer la liste des clients
    useEffect(() => {
        const loadClients = async () => {
            try {
                const token = localStorage.getItem("expert_token");
                // console.log("Token récupéré :", token);

                if (!token) {
                    console.warn("Token manquant");
                    setError("Session expirée. Veuillez vous reconnecter.");
                    return;
                }

                const res = await fetch("https://api-ec.cinef-expertises.com/api/clients", {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Accept": "application/json",
                    },
                });

                if (!res.ok) {
                    throw new Error(`Erreur HTTP ${res.status}: ${res.statusText}`);
                }

                const data = await res.json();
                const clientsList = data?.data ?? data;

                setClients(Array.isArray(clientsList) ? clientsList : []);

            } catch (err) {
                console.error("Erreur lors du chargement des clients:", err);
                setError(`Impossible de charger la liste des clients: ${err.message}`);
                setClients([]);
            }
        };

        loadClients();
    }, []);

    // Gestion du formulaire standard
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    // Ajouter un détail financier
    const handleAddDetailFinancier = (e) => {
        if (e.key === 'Enter' || e.type === 'click') {
            e.preventDefault();

            if (!selectedDetailType) {
                alert("Veuillez sélectionner un type de détail financier");
                return;
            }

            if (selectedDetailType === "autres" && !autreDetailLabel.trim()) {
                alert("Veuillez spécifier le type de détail financier");
                return;
            }

            if (!detailMontant || parseFloat(detailMontant) <= 0) {
                alert("Veuillez entrer un montant valide");
                return;
            }

            const label = selectedDetailType === "autres"
                ? autreDetailLabel
                : detailsFinanciersOptions.find(opt => opt.value === selectedDetailType)?.label;

            const newDetail = {
                type: selectedDetailType === "autres" ? autreDetailLabel : selectedDetailType,
                label: label,
                montant: parseFloat(detailMontant)
            };

            setDetailsFinanciers(prev => [...prev, newDetail]);

            // Reset
            setSelectedDetailType("");
            setDetailMontant("");
            setAutreDetailLabel("");
        }
    };

    // Supprimer un détail financier
    const handleRemoveDetailFinancier = (index) => {
        setDetailsFinanciers(prev => prev.filter((_, i) => i !== index));
    };

    // Ajouter une évolution mensuelle
    const handleAddEvolution = (e) => {
        if (e.key === 'Enter' || e.type === 'click') {
            e.preventDefault();

            if (!selectedMois) {
                alert("Veuillez sélectionner un mois");
                return;
            }

            if (!evolutionMontant || parseFloat(evolutionMontant) <= 0) {
                alert("Veuillez entrer un montant valide");
                return;
            }

            // Vérifier si le mois existe déjà
            const existingIndex = evolutionMensuelle.findIndex(ev => ev.mois === selectedMois);

            if (existingIndex !== -1) {
                // Mettre à jour le montant existant
                const updated = [...evolutionMensuelle];
                updated[existingIndex].montant = parseFloat(evolutionMontant);
                setEvolutionMensuelle(updated);
            } else {
                // Ajouter nouveau mois
                const label = moisOptions.find(m => m.value === selectedMois)?.label;
                const newEvolution = {
                    mois: selectedMois,
                    label: label,
                    montant: parseFloat(evolutionMontant)
                };
                setEvolutionMensuelle(prev => [...prev, newEvolution]);
            }

            // Reset
            setSelectedMois("");
            setEvolutionMontant("");
        }
    };

    // Supprimer une évolution mensuelle
    const handleRemoveEvolution = (index) => {
        setEvolutionMensuelle(prev => prev.filter((_, i) => i !== index));
    };

    //   Soumission vers Laravel
    const handleSubmit = async () => {
        setLoading(true);
        setError("");
        setSuccess("");

        // Validation frontend
        if (!form.client_id) {
            setError("Veuillez sélectionner un client");
            setLoading(false);
            return;
        }
        
        if (!form.mois) {
            setError("Veuillez sélectionner un mois");
            setLoading(false);
            return;
        }

        try {
            // Convertir les détails financiers en objet JSON
            const detailsObj = {};
            detailsFinanciers.forEach(detail => {
                detailsObj[detail.type] = detail.montant;
            });

            // Convertir l'évolution en objet JSON
            const evolutionObj = {};
            evolutionMensuelle.forEach(ev => {
                evolutionObj[ev.mois] = ev.montant;
            });

            const payload = {
                ...form,
                details_financiers: Object.keys(detailsObj).length > 0 ? detailsObj : null,
                evolution: Object.keys(evolutionObj).length > 0 ? evolutionObj : null,
            };
            console.log("Payload envoyé :", payload);
            console.log("Token utilisé :", token);
            console.log("Token existe ?", !!token);
            
            if (!token) {
                throw new Error("Token d'authentification manquant. Veuillez vous reconnecter.");
            }

            const res = await fetch("https://api-ec.cinef-expertises.com/api/donnees-mensuelles", {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload)
            });
           

            const data = await res.json();


            if (!res.ok) {
                throw new Error(data.message || "Erreur lors de l'enregistrement");
            }

            setSuccess("Données mensuelles enregistrées !");

            // Reset complet
            setForm({
                client_id: "",
                mois: "",
                annee: new Date().getFullYear(),
                ca_mensuel: "",
                ca_cumule: "",
                resultat_mensuel: "",
                resultat_cumule: "",
                tresorerie: "",
                details_financiers: "",
                evolution: "",
                commentaire: "",
            });
            setDetailsFinanciers([]);
            setEvolutionMensuelle([]);

        } catch (err) {
            setError(err.message);
        }

        setLoading(false);
    };

    return (
        <>
            <div className="app-container">
                <SidebarComptable
                    isOpen={isSidebarOpen}
                    onClose={() => setIsSidebarOpen(false)}
                />

                <div className="main-wrapper">
                    <NavbarComptable
                        selectedClient={selectedClient}
                        onClientChange={setSelectedClient}
                        clients={clients}
                        onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
                    />

                    <main className="main-content">
                        <div className="page-header">
                            <div className="header-content">
                                <div className="header-text">
                                    <h1 className="page-title">Créer des données mensuelles</h1>
                                    <p className="page-subtitle">Enregistrez les données financières mensuelles de vos clients</p>
                                </div>
                            </div>
                        </div>

                        <div className="donnees-container">
                            {error && (
                                <div className="alert alert-error">
                                    {error}
                                </div>
                            )}
                            {success && (
                                <div className="alert alert-success">
                                    {success}
                                </div>
                            )}

                            <div className="donnees-content">
                                <div className="donnees-section">
                                    <div className="section-header">
                                        <h3 className="section-title">Informations Générales</h3>
                                    </div>
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label className="form-label">Client</label>
                                            <select
                                                name="client_id"
                                                value={form.client_id}
                                                onChange={handleChange}
                                                className="form-select"
                                            >
                                                <option value="">-- Choisir un client --</option>
                                                {clients.map((c) => (
                                                    <option key={c.id} value={c.id}>{c.nom}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Mois</label>
                                            <select
                                                name="mois"
                                                value={form.mois}
                                                onChange={handleChange}
                                                className="form-select"
                                            >
                                                <option value="">-- Mois --</option>
                                                {[...Array(12).keys()].map(m => (
                                                    <option key={m + 1} value={m + 1}>{m + 1}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Année</label>
                                            <input
                                                type="number"
                                                name="annee"
                                                value={form.annee}
                                                onChange={handleChange}
                                                className="form-input"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="donnees-section">
                                    <div className="section-header">
                                        <h3 className="section-title">Chiffre d'Affaires (CFA)</h3>
                                    </div>
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label className="form-label">CA mensuel (XOF)</label>
                                            <input
                                                type="number"
                                                name="ca_mensuel"
                                                value={form.ca_mensuel}
                                                onChange={handleChange}
                                                className="form-input"
                                                placeholder="0 XOF"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">CA cumulé (XOF)</label>
                                            <input
                                                type="number"
                                                name="ca_cumule"
                                                value={form.ca_cumule}
                                                onChange={handleChange}
                                                className="form-input"
                                                placeholder="0 XOF"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="donnees-section">
                                    <div className="section-header">
                                        <h3 className="section-title">Résultats (CFA)</h3>
                                    </div>
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label className="form-label">Résultat mensuel (XOF)</label>
                                            <input
                                                type="number"
                                                name="resultat_mensuel"
                                                value={form.resultat_mensuel}
                                                onChange={handleChange}
                                                className="form-input"
                                                placeholder="0 XOF"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Résultat cumulé (XOF)</label>
                                            <input
                                                type="number"
                                                name="resultat_cumule"
                                                value={form.resultat_cumule}
                                                onChange={handleChange}
                                                className="form-input"
                                                placeholder="0 XOF"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="donnees-section">
                                    <div className="section-header">
                                        <h3 className="section-title">Trésorerie (CFA)</h3>
                                    </div>
                                    <div className="form-grid">
                                        <div className="form-group full-width">
                                            <label className="form-label">Trésorerie (XOF)</label>
                                            <input
                                                type="number"
                                                name="tresorerie"
                                                value={form.tresorerie}
                                                onChange={handleChange}
                                                className="form-input"
                                                placeholder="0 XOF"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="donnees-section">
                                    <div className="section-header">
                                        <h3 className="section-title">Détails Financiers (CFA)</h3>
                                    </div>

                                    <div className="dynamic-input-container">
                                        <div className="dynamic-input-row">
                                            <div className="form-group">
                                                <label className="form-label">Type de détail</label>
                                                <select
                                                    value={selectedDetailType}
                                                    onChange={(e) => setSelectedDetailType(e.target.value)}
                                                    className="form-select"
                                                >
                                                    <option value="">-- Sélectionner --</option>
                                                    {detailsFinanciersOptions.map(opt => (
                                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            {selectedDetailType === "autres" && (
                                                <div className="form-group">
                                                    <label className="form-label">Préciser le type</label>
                                                    <input
                                                        type="text"
                                                        value={autreDetailLabel}
                                                        onChange={(e) => setAutreDetailLabel(e.target.value)}
                                                        className="form-input"
                                                        placeholder="Ex: Frais de déplacement"
                                                    />
                                                </div>
                                            )}

                                            <div className="form-group">
                                                <label className="form-label">Montant (XOF)</label>
                                                <input
                                                    type="number"
                                                    value={detailMontant}
                                                    onChange={(e) => setDetailMontant(e.target.value)}
                                                    onKeyPress={handleAddDetailFinancier}
                                                    className="form-input"
                                                    placeholder="0 XOF"
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label className="form-label" style={{ opacity: 0 }}>Action</label>
                                                <button
                                                    type="button"
                                                    onClick={handleAddDetailFinancier}
                                                    className="add-button"
                                                >
                                                    Ajouter
                                                </button>
                                            </div>
                                        </div>

                                        {detailsFinanciers.length > 0 && (
                                            <div className="added-items">
                                                <h4 className="added-items-title">Détails ajoutés :</h4>
                                                {detailsFinanciers.map((detail, index) => (
                                                    <div key={index} className="added-item">
                                                        <span className="added-item-label">{detail.label}</span>
                                                        <span className="added-item-montant">{detail.montant.toLocaleString()} XOF</span>
                                                        <button
                                                            onClick={() => handleRemoveDetailFinancier(index)}
                                                            className="remove-button"
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="donnees-section">
                                    <div className="section-header">
                                        <h3 className="section-title">Évolution Mensuelle (CFA)</h3>
                                    </div>

                                    <div className="dynamic-input-container">
                                        <div className="dynamic-input-row">
                                            <div className="form-group">
                                                <label className="form-label">Mois</label>
                                                <select
                                                    value={selectedMois}
                                                    onChange={(e) => setSelectedMois(e.target.value)}
                                                    className="form-select"
                                                >
                                                    <option value="">-- Sélectionner un mois --</option>
                                                    {moisOptions.map(m => (
                                                        <option key={m.value} value={m.value}>{m.label}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="form-group">
                                                <label className="form-label">Montant (XOF)</label>
                                                <input
                                                    type="number"
                                                    value={evolutionMontant}
                                                    onChange={(e) => setEvolutionMontant(e.target.value)}
                                                    onKeyPress={handleAddEvolution}
                                                    className="form-input"
                                                    placeholder="0 XOF"
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label className="form-label" style={{ opacity: 0 }}>Action</label>
                                                <button
                                                    type="button"
                                                    onClick={handleAddEvolution}
                                                    className="add-button"
                                                >
                                                    Ajouter
                                                </button>
                                            </div>
                                        </div>

                                        {evolutionMensuelle.length > 0 && (
                                            <div className="added-items">
                                                <h4 className="added-items-title">Évolutions ajoutées :</h4>
                                                {evolutionMensuelle.map((ev, index) => (
                                                    <div key={index} className="added-item">
                                                        <span className="added-item-label">{ev.label}</span>
                                                        <span className="added-item-montant">{ev.montant.toLocaleString()} XOF</span>
                                                        <button
                                                            onClick={() => handleRemoveEvolution(index)}
                                                            className="remove-button"
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="donnees-section">
                                    <div className="section-header">
                                        <h3 className="section-title">Commentaire</h3>
                                    </div>
                                    <div className="form-grid">
                                        <div className="form-group full-width">
                                            <label className="form-label">Commentaire du comptable</label>
                                            <textarea
                                                name="commentaire"
                                                value={form.commentaire}
                                                onChange={handleChange}
                                                className="form-textarea"
                                                placeholder="Ajoutez vos observations..."
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-actions">
                                    <button
                                        onClick={handleSubmit}
                                        disabled={loading}
                                        className="submit-button"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="spinner" size={20} />
                                                Enregistrement en cours...
                                            </>
                                        ) : (
                                            "Créer les données mensuelles"
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }

                .app-container {
                    display: flex;
                    height: 100vh;
                    background: linear-gradient(135deg, #f8fafb 0%, #f1f5f9 100%);
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }

                .main-wrapper {
          flex: 1;
          display: flex;
          flex-direction: column;
          margin-left: 320px;
        }


                .main-content {
                    flex: 1;
                    padding: 32px;
                    overflow-y: auto;
                    scrollbar-width: thin;
                    scrollbar-color: rgba(0, 138, 68, 0.3) transparent;
                }

                .main-content::-webkit-scrollbar {
                    width: 6px;
                }

                .main-content::-webkit-scrollbar-track {
                    background: transparent;
                }

                .main-content::-webkit-scrollbar-thumb {
                    background: rgba(0, 138, 68, 0.2);
                    border-radius: 10px;
                }

                .page-header {
                    margin-bottom: 40px;
                }

                .header-content {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                }

                .header-text {
                    flex: 1;
                }

                .page-title {
                    font-size: 32px;
                    font-weight: 700;
                    margin: 0 0 6px 0;
                    background: linear-gradient(135deg, #dc3545, #008A44);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    line-height: 1.2;
                    letter-spacing: -0.5px;
                }

                .page-subtitle {
                    color: #64748b;
                    margin: 0;
                    font-size: 16px;
                    font-weight: 500;
                }

                .donnees-container {
                    background: #ffffff;
                    border-radius: 16px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
                    border: 1px solid rgba(0, 0, 0, 0.05);
                    overflow: hidden;
                }

                .alert {
                    padding: 16px 28px;
                    margin: 0;
                    font-size: 14px;
                    font-weight: 500;
                    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
                }

                .alert-error {
                    background: #fee2e2;
                    color: #dc2626;
                }

                .alert-success {
                    background: #d1fae5;
                    color: #059669;
                }

                .donnees-content {
                    padding: 28px;
                }

                .donnees-section {
                    margin-bottom: 32px;
                }

                .donnees-section:last-of-type {
                    margin-bottom: 0;
                }

                .section-header {
                    margin-bottom: 20px;
                }

                .section-title {
                    font-size: 18px;
                    font-weight: 700;
                    color: #374151;
                    margin: 0;
                    padding-bottom: 8px;
                    border-bottom: 2px solid #f1f5f9;
                }

                .form-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                    gap: 20px;
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .form-group.full-width {
                    grid-column: 1 / -1;
                }

                .form-label {
                    font-size: 14px;
                    font-weight: 600;
                    color: #374151;
                }

                .form-input,
                .form-select,
                .form-textarea {
                    padding: 12px 16px;
                    border: 2px solid #e2e8f0;
                    border-radius: 12px;
                    background: #ffffff;
                    font-size: 14px;
                    font-weight: 500;
                    color: #374151;
                    font-family: 'Inter', sans-serif;
                    transition: all 0.2s ease;
                }

                .form-input:focus,
                .form-select:focus,
                .form-textarea:focus {
                    outline: none;
                    border-color: #10b981;
                    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
                }

                .form-select {
                    color: #000 !important;
                    background-color: #ffffff !important;
                }

                .form-select option {
                    color: #000 !important;
                    background-color: #ffffff !important;
                }

                .form-textarea {
                    min-height: 100px;
                    resize: vertical;
                }

                .dynamic-input-container {
                    background: #f8fafb;
                    border-radius: 12px;
                    padding: 20px;
                    border: 1px solid #e2e8f0;
                }

                .dynamic-input-row {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 16px;
                    margin-bottom: 20px;
                }

                .add-button {
                    padding: 12px 20px;
                    background: linear-gradient(135deg, #10b981, #059669);
                    color: white;
                    border: none;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    height: fit-content;
                }

                .add-button:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
                }

                .added-items {
                    margin-top: 20px;
                    border-top: 1px solid #e2e8f0;
                    padding-top: 16px;
                }

                .added-items-title {
                    font-size: 14px;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 12px;
                }

                .added-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 12px 16px;
                    background: white;
                    border-radius: 8px;
                    margin-bottom: 8px;
                    border: 1px solid #e2e8f0;
                    transition: all 0.2s ease;
                }

                .added-item:hover {
                    border-color: #cbd5e1;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                }

                .added-item-label {
                    font-size: 14px;
                    font-weight: 600;
                    color: #374151;
                    flex: 1;
                }

                .added-item-montant {
                    font-size: 14px;
                    font-weight: 700;
                    color: #10b981;
                    margin-right: 12px;
                }

                .remove-button {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 28px;
                    height: 28px;
                    background: #fee2e2;
                    color: #dc2626;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .remove-button:hover {
                    background: #fecaca;
                    transform: scale(1.1);
                }

                .form-actions {
                    margin-top: 32px;
                    padding-top: 24px;
                    border-top: 1px solid #f1f5f9;
                }

                .submit-button {
                    width: 100%;
                    padding: 16px 24px;
                    background: linear-gradient(135deg, #dc3545, #008A44);
                    color: #ffffff;
                    font-size: 16px;
                    font-weight: 700;
                    border: none;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    box-shadow: 0 4px 12px rgba(220, 53, 69, 0.2);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                }

                .submit-button:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(220, 53, 69, 0.3);
                }

                .submit-button:active:not(:disabled) {
                    transform: translateY(0);
                }

                .submit-button:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .spinner {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    from {
                        transform: rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg);
                    }
                }

                @media (max-width: 768px) {
                    .main-content {
                        padding: 20px;
                    }

                    .page-title {
                        font-size: 24px;
                    }

                    .donnees-content {
                        padding: 20px;
                    }

                    .form-grid {
                        grid-template-columns: 1fr;
                    }

                    .dynamic-input-row {
                        grid-template-columns: 1fr;
                    }

                    .dynamic-input-container {
                        padding: 16px;
                    }
                }
            `}</style>
        </>
    );
}

export default MonthlyDataComptable;